// Blog JSON-LD builders (blog home, article, listing, author). Pure, no Next / Apollo import, so `node --test` loads it.
// Moved to core from chawk-bazar / ciseco (`helpers/blogJsonLd.js`), where they were written to Google's Article / Profile
// page rules: only real data, every empty field is left out, nodes do not reference each other by `{"@id"}` (the
// validator nests a referenced node), `BreadcrumbList` is left to the page's breadcrumb component.
//
// The builders take small models (a card, an article, an author). chawk-bazar / ciseco pass their own `read_blog_card` /
// `read_blog_article` / `read_author` output; any other site turns a raw backend record into the same model with
// `json_ld_blog_card`, `json_ld_blog_article` and `json_ld_author` at the end of this file. Persian comments inside the
// builders are kept from the original.
// same-folder relative import: this file is loaded by plain `node --test` too, where `@core` does not resolve
import { social_profile_url } from "./sosialMedias.js";

const MAX_ITEMS = 20;

const drop_empty = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(([, value]) => {
      if (value === undefined || value === null || value === "") return false;
      if (Array.isArray(value)) return value.length > 0;
      return true;
    }),
  );

const absolute_url = (origin, path) => {
  if (typeof path !== "string" || !path) return undefined;
  if (/^https?:\/\//i.test(path)) return path;
  if (!path.startsWith("/")) return undefined;
  return `${String(origin || "").replace(/\/+$/, "")}${path}`;
};

const media_url = (prefix, url) => {
  if (typeof url !== "string" || !url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  return `${String(prefix || "").replace(/\/+$/, "")}/${url.replace(/^\/+/, "")}`;
};

/** مقاله‌ی کارت (`read_blog_card`) → `BlogPosting` مختصر (headline، url، تاریخ، تصویر، نویسنده). */
export const build_blog_posting_summary = (card, { origin = "", storage_prefix_url = "" } = {}) => {
  const url = absolute_url(origin, card?.href);
  if (!url || !card?.title) return null;
  return drop_empty({
    "@type": "BlogPosting",
    headline: card.title,
    url,
    datePublished: card.date_iso,
    image: card.image?.url ? media_url(storage_prefix_url, card.image.url) : undefined,
    author: card.author?.name ? drop_empty({ "@type": "Person", name: card.author.name, url: absolute_url(origin, card.author.href) }) : undefined,
  });
};

/**
 * @param {object} options
 * @param {string} options.origin                مثل `https://example.com`
 * @param {string} options.page_path             مسیر خانه‌ی بلاگ
 * @param {string} [options.home_path]           مسیر خانه‌ی سایت (برای `isPartOf`)
 * @param {string} [options.site_name]           نام سایت (ناشر و WebSite)
 * @param {string} options.title                 عنوان صفحه
 * @param {string} [options.description]
 * @param {string} [options.in_language]
 * @param {string} [options.storage_prefix_url]
 * @param {Array}  options.cards                 مقاله‌های همین صفحه (`read_blog_card`) به ترتیب نمایش
 */
export const build_blog_home_json_ld = ({ origin = "", page_path = "/", home_path = "/", site_name, title, description, in_language, storage_prefix_url = "", cards = [] } = {}) => {
  const base = String(origin || "").replace(/\/+$/, "");
  const page_url = `${base}${page_path.endsWith("/") ? page_path : `${page_path}/`}`;
  const home_url = `${base}${(home_path || "/").replace(/\/{2,}/g, "/")}`;

  const postings = (Array.isArray(cards) ? cards : [])
    .slice(0, MAX_ITEMS)
    .map((card) => ({ card, posting: build_blog_posting_summary(card, { origin, storage_prefix_url }) }))
    .filter((item) => item.posting);

  const items = postings.map(({ posting }, index) => ({
    "@type": "ListItem",
    position: index + 1,
    url: posting.url,
    name: posting.headline,
  }));

  const text = typeof description === "string" ? description.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() : "";
  const website = site_name ? drop_empty({ "@type": "WebSite", "@id": `${home_url}#website`, url: home_url, name: site_name }) : undefined;

  const collection_page = drop_empty({
    "@type": "CollectionPage",
    "@id": `${page_url}#webpage`,
    url: page_url,
    name: title || site_name,
    description: text,
    inLanguage: in_language,
    isPartOf: website,
    mainEntity: items.length ? { "@type": "ItemList", numberOfItems: items.length, itemListElement: items } : undefined,
  });

  const blog = drop_empty({
    "@type": "Blog",
    "@id": `${page_url}#blog`,
    url: page_url,
    name: title || site_name,
    description: text,
    inLanguage: in_language,
    publisher: site_name ? { "@type": "Organization", name: site_name } : undefined,
    blogPost: postings.map(({ posting }) => posting),
  });

  return { "@context": "https://schema.org", "@graph": [collection_page, blog] };
};

// ===== مقاله‌ی تکی (۲.۱۳) =====

const iso_duration = (minutes) => (minutes > 0 ? `PT${Math.round(minutes)}M` : undefined);

/**
 * اسکیمای `BlogPosting` مقاله (قوانین Article گوگل). همه‌ی مقدارها داده‌ی واقعی‌اند و فیلد خالی حذف می‌شود:
 * - `headline` عنوان مقاله، `image` تصویر شاخص (با ابعاد واقعی وقتی بک‌اند دارد)، `datePublished` و `dateModified` ISO با منطقه‌ی زمانی
 *   (`dateModified` هیچ‌وقت قبل از انتشار نیست)، `author` نویسنده‌ی مقاله (`Person` با `url` صفحه‌ی نویسنده)؛ مقاله‌ی بی‌نویسنده به نام سایت
 *   (`Organization`) منتشر می‌شود.
 * - `publisher` سازمان سایت با لوگو (فقط وقتی لوگو هست)، `isPartOf` همان `Blog` خانه‌ی بلاگ (`helpers/blogJsonLd` خانه) وقتی مسیرش معلوم است.
 * - `keywords` کلیدواژه‌های سئوی مقاله (رشته‌ی جداشده با ویرگول)، `articleSection` دسته، `wordCount` و `timeRequired` از متن واقعی صفحه،
 *   `commentCount` تعداد نظرهای ثبت‌شده. `BreadcrumbList` را کامپوننت `BreadCrumb` (همان مسیر دیده‌شده) می‌دهد.
 * @param {object} options
 * @param {string} options.origin
 * @param {string} options.page_path             مسیر همین مقاله
 * @param {string} [options.home_path]
 * @param {string} [options.blog_home_path]      مسیر خانه‌ی بلاگ (برای `isPartOf`)
 * @param {string} [options.blog_title]
 * @param {string} [options.site_name]
 * @param {string} [options.logo_url]            آدرس مطلق لوگوی سایت
 * @param {object} options.article               خروجی `read_blog_article`
 * @param {string} [options.in_language]
 * @param {string} [options.storage_prefix_url]
 * @param {number} [options.word_count]
 * @param {number} [options.reading_minutes]
 */
export const build_blog_article_json_ld = ({
  origin = "",
  page_path = "/",
  home_path = "/",
  blog_home_path,
  blog_title,
  site_name,
  logo_url,
  article,
  in_language,
  storage_prefix_url = "",
  word_count,
  reading_minutes,
} = {}) => {
  if (!article?.title) return null;

  const base = String(origin || "").replace(/\/+$/, "");
  const page_url = `${base}${page_path.endsWith("/") ? page_path : `${page_path}/`}`;
  const home_url = `${base}${(home_path || "/").replace(/\/{2,}/g, "/")}`;
  const blog_url = absolute_url(origin, blog_home_path);

  const image_url = article.image?.url ? media_url(storage_prefix_url, article.image.url) : undefined;
  const image = image_url
    ? drop_empty({ "@type": "ImageObject", url: image_url, width: article.image.width || undefined, height: article.image.height || undefined })
    : undefined;

  const author = article.author
    ? drop_empty({ "@type": "Person", name: article.author.name, url: absolute_url(origin, article.author.href) })
    : site_name
      ? drop_empty({ "@type": "Organization", name: site_name, url: home_url })
      : undefined;

  const publisher = site_name
    ? drop_empty({
        "@type": "Organization",
        name: site_name,
        url: home_url,
        logo: logo_url ? { "@type": "ImageObject", url: logo_url } : undefined,
      })
    : undefined;

  const is_part_of = blog_url
    ? drop_empty({ "@type": "Blog", "@id": `${blog_url.endsWith("/") ? blog_url : `${blog_url}/`}#blog`, url: blog_url, name: blog_title })
    : undefined;

  const description = typeof article.description === "string" ? article.description.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() : "";

  return {
    "@context": "https://schema.org",
    ...drop_empty({
      "@type": "BlogPosting",
      "@id": `${page_url}#article`,
      mainEntityOfPage: { "@type": "WebPage", "@id": page_url },
      url: page_url,
      headline: article.title,
      description,
      image: image ? [image] : undefined,
      datePublished: article.published_iso,
      dateModified: article.modified_iso || article.published_iso,
      author,
      publisher,
      isPartOf: is_part_of,
      articleSection: article.category?.title,
      keywords: article.keywords?.length ? article.keywords.join(", ") : undefined,
      wordCount: word_count > 0 ? word_count : undefined,
      timeRequired: iso_duration(reading_minutes),
      commentCount: article.comments_count > 0 ? article.comments_count : undefined,
      inLanguage: in_language,
    }),
  };
};

// ===== فهرست مقاله‌ها: دسته و جستجو (۲.۱۴) =====

/**
 * اسکیمای صفحه‌ی فهرست مقاله (دسته یا جستجو): `CollectionPage` + `ItemList` مقاله‌های همین صفحه. `position` ادامه‌ی صفحه‌های قبل است
 * (صفحه‌ی ۲ از `limit + 1` شروع می‌شود) و `numberOfItems` تعداد کل فهرست است. فقط داده‌ی واقعی؛ فیلد خالی حذف می‌شود و بدون هیچ
 * مقاله‌ی معتبر `mainEntity` نمی‌آید. `BreadcrumbList` را `BreadCrumb` می‌دهد. `isPartOf` خانه‌ی بلاگ است (وقتی مسیرش معلوم است)،
 * وگرنه WebSite سایت.
 * @param {object} options
 * @param {string} options.origin
 * @param {string} options.page_path             مسیر همین صفحه با `?page=N` برای صفحه‌ی ۲ به بعد (= canonical)
 * @param {string} [options.home_path]           مسیر خانه‌ی سایت
 * @param {string} [options.blog_home_path]      مسیر خانه‌ی بلاگ
 * @param {string} [options.blog_title]
 * @param {string} [options.site_name]
 * @param {string} options.title
 * @param {string} [options.description]
 * @param {string} [options.in_language]
 * @param {Array}  options.cards                 مقاله‌های همین صفحه (`read_blog_card`)
 * @param {number} [options.page]
 * @param {number} [options.limit]
 * @param {number} [options.total]
 */
export const build_blog_listing_json_ld = ({
  origin = "",
  page_path = "/",
  home_path = "/",
  blog_home_path,
  blog_title,
  site_name,
  title,
  description,
  in_language,
  cards = [],
  page = 1,
  limit = 12,
  total,
} = {}) => {
  const base = String(origin || "").replace(/\/+$/, "");
  const page_url = `${base}${page_path}`;
  const home_url = `${base}${(home_path || "/").replace(/\/{2,}/g, "/")}`;
  const blog_url = absolute_url(origin, blog_home_path);
  const offset = (Math.max(parseInt(page) || 1, 1) - 1) * limit;

  const items = (Array.isArray(cards) ? cards : [])
    .map((card) => ({ url: absolute_url(origin, card?.href), name: card?.title }))
    .filter((item) => item.url && item.name)
    .map((item, index) => ({ "@type": "ListItem", position: offset + index + 1, url: item.url, name: item.name }));

  const text = typeof description === "string" ? description.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() : "";

  const is_part_of = blog_url
    ? drop_empty({ "@type": "Blog", "@id": `${blog_url.endsWith("/") ? blog_url : `${blog_url}/`}#blog`, url: blog_url, name: blog_title })
    : site_name
      ? drop_empty({ "@type": "WebSite", "@id": `${home_url}#website`, url: home_url, name: site_name })
      : undefined;

  return {
    "@context": "https://schema.org",
    ...drop_empty({
      "@type": "CollectionPage",
      "@id": `${page_url}#webpage`,
      url: page_url,
      name: title || site_name,
      description: text,
      inLanguage: in_language,
      isPartOf: is_part_of,
      mainEntity: items.length
        ? drop_empty({ "@type": "ItemList", numberOfItems: total > 0 ? total : undefined, itemListElement: items })
        : undefined,
    }),
  };
};

// ===== نویسنده (۲.۱۵) =====

/**
 * اسکیمای صفحه‌ی نویسنده طبق قوانین Profile page گوگل: `ProfilePage` با `mainEntity` از نوع `Person` (نام، آدرس پروفایل، تصویر، توضیح،
 * `sameAs` = فقط شبکه‌های اجتماعی واقعیِ ثبت‌شده برای او، و تعداد مقاله‌های نوشته‌شده به‌صورت `agentInteractionStatistic` / `WriteAction`) +
 * `ItemList` مقاله‌های همین صفحه (`position` ادامه‌ی صفحه‌های قبل). فقط چیزی که پروفایل عمومی نشان می‌دهد؛ هیچ ایمیل، تلفن یا فیلد خصوصی نمی‌آید.
 * فیلد خالی حذف می‌شود و بدون مقاله‌ی معتبر `ItemList` ساخته نمی‌شود. `BreadcrumbList` را `BreadCrumb` می‌دهد.
 * @param {object} options
 * @param {string} options.origin
 * @param {string} options.page_path             مسیر همین صفحه با `?page=N` برای صفحه‌ی ۲ به بعد (= canonical)
 * @param {string} options.author_path           مسیر پروفایل نویسنده بدون query (آدرس `Person`)
 * @param {string} [options.home_path]
 * @param {string} [options.blog_home_path]
 * @param {string} [options.blog_title]
 * @param {string} [options.site_name]
 * @param {object} options.author                خروجی `read_author`
 * @param {string} [options.description]         توضیح کوتاه (متن معرفی یا جمله‌ی پیش‌فرض)
 * @param {string} [options.list_name]           نام `ItemList`
 * @param {string} [options.in_language]
 * @param {string} [options.storage_prefix_url]
 * @param {Array}  options.cards
 * @param {number} [options.page]
 * @param {number} [options.limit]
 * @param {number} [options.total]               تعداد کل مقاله‌های نویسنده
 */
export const build_author_json_ld = ({
  origin = "",
  page_path = "/",
  author_path,
  home_path = "/",
  blog_home_path,
  blog_title,
  site_name,
  author,
  description,
  list_name,
  in_language,
  storage_prefix_url = "",
  cards = [],
  page = 1,
  limit = 12,
  total,
} = {}) => {
  if (!author?.name) return null;

  const base = String(origin || "").replace(/\/+$/, "");
  const page_url = `${base}${page_path}`;
  const author_url = absolute_url(origin, author_path) || page_url.split("?")[0];
  const home_url = `${base}${(home_path || "/").replace(/\/{2,}/g, "/")}`;
  const blog_url = absolute_url(origin, blog_home_path);
  const offset = (Math.max(parseInt(page) || 1, 1) - 1) * limit;
  const text = typeof description === "string" ? description.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() : "";

  const image_url = author.avatar?.url ? media_url(storage_prefix_url, author.avatar.url) : undefined;
  const same_as = [...new Set((Array.isArray(author.socials) ? author.socials : []).map((item) => item?.href).filter((href) => /^https:\/\//.test(href || "")))];

  const person = drop_empty({
    "@type": "Person",
    "@id": `${author_url}#person`,
    name: author.name,
    givenName: author.first_name,
    familyName: author.last_name,
    url: author_url,
    image: image_url ? { "@type": "ImageObject", url: image_url } : undefined,
    description: text,
    sameAs: same_as,
    agentInteractionStatistic:
      total > 0 ? { "@type": "InteractionCounter", interactionType: "https://schema.org/WriteAction", userInteractionCount: total } : undefined,
  });

  const is_part_of = blog_url
    ? drop_empty({ "@type": "Blog", "@id": `${blog_url.endsWith("/") ? blog_url : `${blog_url}/`}#blog`, url: blog_url, name: blog_title })
    : site_name
      ? drop_empty({ "@type": "WebSite", "@id": `${home_url}#website`, url: home_url, name: site_name })
      : undefined;

  const profile_page = drop_empty({
    "@type": "ProfilePage",
    "@id": `${page_url}#profilepage`,
    url: page_url,
    name: author.name,
    inLanguage: in_language,
    isPartOf: is_part_of,
    mainEntity: person,
  });

  const items = (Array.isArray(cards) ? cards : [])
    .map((card) => ({ url: absolute_url(origin, card?.href), name: card?.title }))
    .filter((item) => item.url && item.name)
    .map((item, index) => ({ "@type": "ListItem", position: offset + index + 1, url: item.url, name: item.name }));

  const item_list = items.length
    ? drop_empty({
        "@type": "ItemList",
        "@id": `${page_url}#articles`,
        name: list_name || author.name,
        numberOfItems: total > 0 ? total : undefined,
        itemListElement: items,
      })
    : undefined;

  return { "@context": "https://schema.org", "@graph": [profile_page, ...(item_list ? [item_list] : [])] };
};

// ===== raw backend record → builder model (for sites without their own models) =====

const plain_text = (value) =>
  typeof value === "string" ? value.replace(/<[^>]*>/g, " ").replace(/&nbsp;|&#160;/g, " ").replace(/\s+/g, " ").trim() : "";

// ms timestamp (number or digit string; a 10-digit one is seconds) or an ISO string → ms, else null
const to_timestamp = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const raw = String(value).trim();
  const number = typeof value === "number" ? value : /^\d+$/.test(raw) ? Number(raw) : Date.parse(raw);
  if (!Number.isFinite(number) || number <= 0) return null;
  return number < 1e11 ? number * 1000 : number;
};

const to_iso = (timestamp) => (timestamp === null ? "" : new Date(timestamp).toISOString());

/** Internal path of a backend `full_url` with a trailing `/` (the core Next config uses `trailingSlash`); null without one. */
export const json_ld_path = (url) => {
  if (typeof url !== "string") return null;
  const value = url.trim().replace(/\s+/g, "");
  if (!value || value === "#" || !value.startsWith("/") || value.startsWith("//")) return null;
  if (/[?#]/.test(value) || value.endsWith("/")) return value;
  return `${value}/`;
};

const size = (value) => {
  const number = parseInt(value);
  return number > 0 && number < 20000 ? number : undefined;
};

/** A raw blog card (`blog_card` selection) → the card model of `build_blog_posting_summary` / listing builders; null without title or link. */
export const json_ld_blog_card = (blog) => {
  const title = plain_text(blog?.title);
  const href = json_ld_path(blog?.seo?.full_url);
  if (!title || !href) return null;
  const author_name = plain_text(blog?.user?.full_name);
  return {
    title,
    href,
    date_iso: to_iso(to_timestamp(blog?.publishAt) ?? to_timestamp(blog?.createdAt)),
    image: blog?.media?.url ? { url: blog.media.url } : null,
    author: author_name ? { name: author_name, href: json_ld_path(blog?.user?.seo?.full_url) } : null,
  };
};

/**
 * A raw article (`getBlogBySlug`) → the article model of `build_blog_article_json_ld`; null without a title.
 * `dateModified` is never before `datePublished`; `keywords` drops the backend's `[null]`.
 */
export const json_ld_blog_article = (blog) => {
  const title = plain_text(blog?.title);
  if (!title) return null;
  const published = to_timestamp(blog?.publishAt) ?? to_timestamp(blog?.createdAt);
  let modified = to_timestamp(blog?.updatedAt) ?? published;
  if (published !== null && modified !== null && modified < published) modified = published;
  const info = blog?.media?.media?.information;
  const author_name = plain_text(blog?.user?.full_name);
  const category_title = plain_text(blog?.category?.title);
  const keywords = Array.isArray(blog?.seo?.keywords)
    ? blog.seo.keywords
    : typeof blog?.seo?.keywords === "string"
      ? blog.seo.keywords.split(",")
      : [];
  return {
    title,
    description: plain_text(blog?.seo?.description) || plain_text(blog?.summary),
    image: blog?.media?.url ? { url: blog.media.url, width: size(info?.width), height: size(info?.height) } : null,
    author: author_name ? { name: author_name, href: json_ld_path(blog?.user?.seo?.full_url) } : null,
    category: category_title ? { title: category_title } : null,
    published_iso: to_iso(published),
    modified_iso: to_iso(modified),
    keywords: [...new Set(keywords.map(plain_text).filter(Boolean))],
    comments_count: Math.max(parseInt(blog?.comments_count) || 0, 0),
  };
};

// keys of `custom_fields` that may hold the author's social list (`[{ type, slug }]` or `[{ "social-media": { type, slug } }]`)
const SOCIAL_LIST_KEYS = ["social_medias", "socials", "social_links", "social-media", "repeatable_fields"];

/** A raw author (`getAuthorBySlug`, core `author` selection) → the author model of `build_author_json_ld`; null without a name. */
export const json_ld_author = (author) => {
  const name = plain_text(author?.full_name) || [plain_text(author?.name), plain_text(author?.last_name)].filter(Boolean).join(" ");
  if (!name) return null;
  const fields = author?.custom_fields && typeof author.custom_fields === "object" ? author.custom_fields : {};
  const socials = SOCIAL_LIST_KEYS.flatMap((key) => (Array.isArray(fields[key]) ? fields[key] : []))
    .map((entry) => (entry?.["social-media"] && typeof entry["social-media"] === "object" ? entry["social-media"] : entry))
    .map((item) => ({ href: social_profile_url(item?.type, item?.slug ?? item?.url ?? item?.value) }))
    .filter((item) => item.href);
  return {
    name,
    first_name: plain_text(author?.name),
    last_name: plain_text(author?.last_name),
    avatar: author?.media?.url ? { url: author.media.url } : null,
    socials,
  };
};
